import React, { useEffect, useMemo, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-modal';
import bonusesModalActions from 'store/bonusesModalStore/bonusesModalActions';
import bonusesModalApi from 'store/bonusesModalStore/bonusesModalApi';
import { store } from 'react-notifications-component';
import FindCustomer from './FindCustomer';
import FillForm from './FillForm';
import ConfirmForm from './ConfirmForm';
import { hasResponseErrorMessage, paymentType } from '../../helpers';

import styles from './pay.modal.module.scss';

import closeModalImage from './assets/CloseModalIcon.svg';
import clientInfoActions from '../../store/clientInfoStore/clientInfoActions';
import GiveAward from './GiveAward';
import { useTranslation } from "react-i18next";
import TransactionsTypes from "../../store/transactionsStore/transactionsTypes";
import instance from '../../api';
import branchApi from '../../store/branchStore/branchApi';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    border: 'none',
    padding: 0,
    borderRadius: 24,
  }
};

export default function PayModal(props) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { info } = useSelector(state => state.branchReducer);
  const { isSendUseCashbackCodePending, isSendedUseCashbackCode } = useSelector(state => state.transactionsReducer);
  const { customer, cStatus } = useSelector(state => state.clientInfoReducer);
  const {
    amount_sum,
    stamp_count,
    bonuses,
    client_cashback,
    payment_status,
    give_award_status,
    comment,
    add_stamp,
    give_award
  } = useSelector(state => state.bonusesModalReducer);

  const {
    isOpen,
    hasClientInfo = false,
    setModal
  } = props;

  const [step, setStep] = useState(hasClientInfo ? 2 : 1);
  const [phone, setPhone] = useState('');
  const [payment, setPayment] = useState(paymentType.Cash);
  const [notRegistered, setNotRegistered] = useState(false);
  const [isStandardBonus, setIsStandardBonus] = useState(true);
  const [useReducedCashback, setUseReducedCashback] = useState(false);
  const [whatsAppCode, setWhatsAppCode] = useState("");
  const [type, setType] = useState('phone');
  const [selectedTag, setSelectedTag] = useState(null);
  const [userTags, setUserTags] = useState([]);
  const [branchTags, setBranchTags] = useState([]);
  const [isDiscountOrder, setIsDiscountOrder] = useState(false);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [childBirthdays, setChildBirthdays] = useState([]);
  const [childBirthdaysPending, setChildBirthdaysPending] = useState(false);
  // State for selecting tag from client's own tags (when client has discount tag)
  const [selectedUserTagId, setSelectedUserTagId] = useState(null);

  useEffect(() => {
    if (bonuses <= 0) {
      dispatch({ type: TransactionsTypes.CLEAR_USE_CASHBACK_CODE_PENDING });
    }
    return () => {
      dispatch({ type: TransactionsTypes.CLEAR_USE_CASHBACK_CODE_PENDING });
    }
  }, [bonuses]);

  // Fetch branch tags when modal opens
  useEffect(() => {
    if (info && info.configuration && info.configuration.tags) {
      setBranchTags(info.configuration.tags);
    }
  }, [info]);

  // Fetch user tags when customer is loaded
  useEffect(() => {
    if (customer && customer.id && customer.phone && info && info.id) {
      const fetchUserTags = async () => {
        try {
          const cleanPhone = customer.phone.replace(/\D/g, '');
          const response = await instance().get(`/promoters/find`, {
            params: {
              phone: cleanPhone,
              branch_id: info.id
            }
          });
          if (response.data && response.data.data && response.data.data.tags) {
            setUserTags(response.data.data.tags);
          }
        } catch (error) {
          console.error('Error fetching user tags:', error);
          setUserTags([]);
        }
      };
      fetchUserTags();
    } else {
      setUserTags([]);
    }
  }, [customer, info]);

  // Fetch user subscriptions when customer is loaded
  useEffect(() => {
    if (customer && customer.id && info && info.id) {
      const fetchUserSubscriptions = async () => {
        try {
          const response = await bonusesModalApi.getUserSubscriptions(info.id, customer.id);
          if (response && response.data) {
            setUserSubscriptions(response.data);
          }
        } catch (error) {
          console.error('Error fetching user subscriptions:', error);
          setUserSubscriptions([]);
        }
      };
      fetchUserSubscriptions();
    } else {
      setUserSubscriptions([]);
    }
  }, [customer, info]);

  // Fetch child birthdays when customer is loaded
  useEffect(() => {
    if (customer && customer.phone && info && info.id) {
      const fetchChildBirthdays = async () => {
        setChildBirthdaysPending(true);
        try {
          const cleanPhone = customer.phone.replace(/\D/g, '');
          const response = await branchApi.getChildBirthdays(info.id, cleanPhone);
          if (response && response.data) {
            setChildBirthdays(response.data);
          }
        } catch (error) {
          console.error('Error fetching child birthdays:', error);
          setChildBirthdays([]);
        } finally {
          setChildBirthdaysPending(false);
        }
      };
      fetchChildBirthdays();
    } else {
      setChildBirthdays([]);
    }
  }, [customer, info]);

  // Reset bonuses when discount order is activated
  useEffect(() => {
    if (isDiscountOrder) {
      dispatch(bonusesModalActions.changeBonuses(0));
    }
  }, [isDiscountOrder, dispatch]);

  const onModalClose = () => {
    if (step === 4) {
      return setStep(2);
    }
    if (!hasClientInfo) {
      dispatch(clientInfoActions.resetClientInfo());
    }

    dispatch(bonusesModalActions.resetState());
    setStep(hasClientInfo ? 2 : 1);
    setWhatsAppCode("");
    setIsStandardBonus(true);
    setUseReducedCashback(false);
    setNotRegistered(false);
    setPhone('');
    setSelectedTag(null);
    setIsDiscountOrder(false);
    setUserTags([]);
    setUserSubscriptions([]);
    setSelectedSubscription(null);
    setChildBirthdays([]);
    setSelectedUserTagId(null);
    setModal(false);
  }

  const addChildBirthday = async (birthdayDate) => {
    if (!info || !customer || !customer.phone) return;
    setChildBirthdaysPending(true);
    try {
      const cleanPhone = customer.phone.replace(/\D/g, '');
      const birthdayTimestamp = new Date(birthdayDate).setUTCHours(0, 0, 0, 0);
      await branchApi.addChildBirthday(info.id, {
        phone: cleanPhone,
        birthday: birthdayTimestamp
      });
      // Refresh child birthdays
      const response = await branchApi.getChildBirthdays(info.id, cleanPhone);
      if (response && response.data) {
        setChildBirthdays(response.data);
      }
    } catch (error) {
      console.error('Error adding child birthday:', error);
    } finally {
      setChildBirthdaysPending(false);
    }
  };

  const deleteChildBirthday = async (birthday) => {
    if (!info || !customer || !customer.phone) return;
    setChildBirthdaysPending(true);
    try {
      const cleanPhone = customer.phone.replace(/\D/g, '');
      await branchApi.deleteChildBirthday(info.id, {
        phone: cleanPhone,
        birthday: birthday
      });
      // Refresh child birthdays
      const response = await branchApi.getChildBirthdays(info.id, cleanPhone);
      if (response && response.data) {
        setChildBirthdays(response.data);
      }
    } catch (error) {
      console.error('Error deleting child birthday:', error);
    } finally {
      setChildBirthdaysPending(false);
    }
  };

  const changeAmountSum = (e) => {
    if (isNaN(Number(e.target.value))) return
    dispatch(bonusesModalActions.changeAmountSum(e.target.value))
  }

  const changeStampCount = (e) => {
    let rsc = customer.received_stamp_count;
    if (!rsc) {
      rsc = 0;
    }
    if (isNaN(Number(e.target.value))) return
    if (Number(e.target.value) <= (info.configuration.award.stamp_count - rsc)) {
      dispatch(bonusesModalActions.changeStampCount(e.target.value))
    }
  }

  const changeBonuses = (e) => {
    if (e.target) {
      if (isNaN(Number(e.target.value))) return
      return dispatch(bonusesModalActions.changeBonuses(e.target.value))
    }

    dispatch(bonusesModalActions.changeBonuses(e));
  }

  const changeComment = (e) => {
    dispatch(bonusesModalActions.changeComment(e.target.value));
  }

  const changeStamp = (value) => {
    dispatch(bonusesModalActions.changeStamp(value));
  }

  const changeAward = (value) => {
    dispatch(bonusesModalActions.changeAward(value));
  }

  const getClientCashback = (amount) => {
    notRegistered ?
      dispatch(bonusesModalActions.getClientCashBack(info.id, 0, amount || 0)) :
        customer?.id ? dispatch(bonusesModalActions.getClientCashBack(info.id, customer.id, amount || 0)) : null
  }

  const getClientAward = () => {
    dispatch(clientInfoActions.getClientAward(customer.id, info));
  }

  const giveAwardToUser = () => {
    if ((stamp_count + customer.received_stamp_count) > info.configuration.award.stamp_count) {
      return store.addNotification({
        title: t('error'),
        message: t('NSPayModal.limit', {stamps: stamp_count + customer.received_stamp_count, limit: info.configuration.award.stamp_count}),
        type: "danger",
        insert: "top",
        container: "top-right",
        animationIn: ["animate__animated", "animate__fadeIn"],
        animationOut: ["animate__animated", "animate__fadeOut"],
        dismiss: {
          duration: 5000,
          onScreen: true
        }
      });
    }
    const data = {
      user_id: customer.id,
      stamp_count: customer.stamp_count
    }

    new Promise((resolve, reject) => {
      dispatch(bonusesModalActions.giveAwardToUser(info.id, customer.award_id, data, resolve, reject));
    })
      .then(() => {
        onModalClose();
      })
      .catch(e => {
        if (hasResponseErrorMessage(e)) {
          store.addNotification({
            title: t('error'),
            message: e.response.data.message,
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
              duration: 5000,
              onScreen: true
            }
          });
        }
        onModalClose();
      })

  }

  const confirmPayment = () => {
    let params = {}

    // Find the selected tag from branch tags
    const selectedBranchTag = selectedTag
      ? branchTags.find(t => t.id.toString() === selectedTag.toString())
      : null;

    // Web always sends the original (pre-discount) amount.
    // The backend handles discount calculation when is_full_amount=true.
    let finalAmount = parseInt(amount_sum);

    if (notRegistered) {
      params = {
        user_phone: '+' + phone,
        amount: finalAmount,
        stamp_count: parseInt(stamp_count),
        comment,
        add_stamp: !!(stamp_count),
        give_award,
        use_increased_cashback: isStandardBonus ? false : true,
      }
    } else {
      params = {
        user_id: customer.id,
        amount: finalAmount,
        bonus_amount: parseInt(bonuses),
        stamp_count: parseInt(stamp_count),
        comment,
        add_stamp: !!(stamp_count),
        give_award,
        use_increased_cashback: isStandardBonus ? false : true,
      }
    }

    if (whatsAppCode && isSendedUseCashbackCode) {
      params.use_balance_code = whatsAppCode;
    }

    // Handle stamp usage
    if (selectedSubscription) {
      params.is_stamp_usage = true;
      params.user_subscription_id = selectedSubscription.id;
    }

    // Handle tag selection - check if it's a discount tag
    if (selectedTag && selectedBranchTag) {
      if (selectedBranchTag.is_discount) {
        // Discount tag selected - set discount order params
        params.is_discount_order = true;
        params.discount_name = selectedBranchTag.name;
        params.tag_id = selectedTag.toString();
      } else {
        // Regular tag selection
        params.tag_id = selectedTag.toString();
      }
    }

    // Handle selection from client's own tags (when client has discount tag)
    if (selectedUserTagId) {
      params.selected_tag_id = selectedUserTagId.toString();

      // Find the selected tag from user's tags and check if it's a discount tag
      const selectedUserTag = userTags.find(t => t.id.toString() === selectedUserTagId.toString());
      if (selectedUserTag && selectedUserTag.is_discount) {
        params.is_discount_order = true;
        params.discount_name = selectedUserTag.name;
      }
    }

    // Web always sends the original (full) amount — tell backend to forward-calculate the discount
    if (params.is_discount_order) {
      params.is_full_amount = true;
    }

    if (info?.configuration?.cashier_reduced_cashback_percent > 0 && useReducedCashback && !params.is_discount_order && !selectedSubscription) {
      params.use_reduced_cashback = true;
    }

    new Promise((resolve, reject) => {
      dispatch(bonusesModalActions.confirmPayment(info.id, params, bonuses, payment, resolve, reject))
    })
      .then(() => {
        setWhatsAppCode("");
        onModalClose();
      })
      .catch(e => {
        if (e.response.data.message === 'not supported number') {
          store.addNotification({
            title: t('error'),
            message: t('Form.correctNumber'),
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
              duration: 5000,
              onScreen: true
            }
          });

          setNotRegistered(false);
          return setStep(1);
        }
        if (hasResponseErrorMessage(e)) {
          store.addNotification({
            title: t('error'),
            message: e.response.data.message,
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
              duration: 5000,
              onScreen: true
            }
          });
        }
      })
      .finally(() => {
      })
  }

  const cashback = useMemo(() => {
    return client_cashback ? client_cashback : 0;
  }, [client_cashback]);

  const increasedCashback = useMemo(() => {
    if (info && info.rule) {
      if (info.rule.increased_cashback.enabled && info.rule.increased_cashback.always_on) {
        return info.rule.increased_cashback.percentage;
      }
    }
    return null;
  }, [info]);

  const renderForm = () => {
    switch (step) {
      case 1:
        return <FindCustomer
          setStep={setStep}
          setNotRegistered={setNotRegistered}
          setPhone={setPhone}
          type={type}
          setType={setType}
        />
      case 2:
        return (
          <FillForm
            cStatus={cStatus}
            setStep={setStep}
            phone={phone}
            customer={customer}
            isSendUseCashbackCodePending={isSendUseCashbackCodePending}
            isSendedUseCashbackCode={isSendedUseCashbackCode}
            amount_sum={amount_sum}
            bonuses={bonuses}
            notRegistered={notRegistered}
            comment={comment}
            payment={payment}
            setPayment={setPayment}
            add_stamp={add_stamp}
            give_award={give_award}
            info={info}
            type={type}
            getClientAward={getClientAward}
            changeStamp={changeStamp}
            changeAward={changeAward}
            changeComment={changeComment}
            changeAmountSum={changeAmountSum}
            changeBonuses={changeBonuses}
            getClientCashback={getClientCashback}
            changeStampCount={changeStampCount}
            stamp_count={stamp_count}
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            userTags={userTags}
            branchTags={branchTags}
            isDiscountOrder={isDiscountOrder}
            setIsDiscountOrder={setIsDiscountOrder}
            userSubscriptions={userSubscriptions}
            selectedSubscription={selectedSubscription}
            setSelectedSubscription={setSelectedSubscription}
            childBirthdays={childBirthdays}
            childBirthdaysPending={childBirthdaysPending}
            addChildBirthday={addChildBirthday}
            deleteChildBirthday={deleteChildBirthday}
            selectedUserTagId={selectedUserTagId}
            setSelectedUserTagId={setSelectedUserTagId}
          />
        )
      case 3:
        return (
          <ConfirmForm
            client_cashback={cashback}
            increasedCashback={increasedCashback}
            setStep={setStep}
            bonuses={bonuses}
            payment_status={payment_status}
            amount_sum={amount_sum}
            notRegistered={notRegistered}
            isStandardBonus={isStandardBonus}
            setIsStandardBonus={setIsStandardBonus}
            useReducedCashback={useReducedCashback}
            setUseReducedCashback={setUseReducedCashback}
            type={type}
            comment={comment}
            payment={payment}
            stamp_count={stamp_count}
            add_stamp={add_stamp}
            give_award={give_award}
            isSendedUseCashbackCode={isSendedUseCashbackCode}
            confirmPayment={confirmPayment}
            whatsAppCode={whatsAppCode}
            setWhatsAppCode={setWhatsAppCode}
            customer={customer}
            selectedSubscription={selectedSubscription}
            isDiscountOrder={isDiscountOrder}
            selectedTag={selectedTag}
            branchTags={branchTags}
            info={info}
            userTags={userTags}
            selectedUserTagId={selectedUserTagId}
          />
        )
      case 4:
        return <GiveAward customer={customer} giveAwardToUser={giveAwardToUser} info={info} />
      default:
        return null
    }
  }

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      style={modalStyles}
      onRequestClose={onModalClose}
    >
      <div className={styles.modal}>
        <div className={styles.closeModal} onClick={onModalClose}>
          <img src={closeModalImage}/>
        </div>
        <div className={styles.container}>
          <Scrollbars style={{ width: '100%', height: '100%' }}>
            {renderForm()}
          </Scrollbars>
        </div>
      </div>
    </Modal>
  )
}
